import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { EmptyStateMessage } from '~components/Common/EmptyStateMessage';
import {
  isMessagingChannelTargetReference,
  MessagingChannel,
} from '~hooks/useMessaging/models';
import { Order } from '~hooks/useOrders/models';
import JobThreadPanel from '~pages/Dispatch/components/JobMessaging/JobThreadPanel';
import OrderJobConversationRow from '~pages/Dispatch/components/OrderMessaging/OrderJobConversationRow';
import { TARGET_MESSAGING_PANEL_HEIGHT_IN_PX } from '~pages/Dispatch/components/TargetMessaging/TargetMessagingPanel';
import useMessagingOrderJobChannelsInfiniteQuery from '~queries/useMessagingOrderJobChannelsInfiniteQuery';
import { useStores } from '~store';
import useInfiniteScroll from '~utils/hooks/useInfiniteScroll';

const LIST_WIDTH_IN_PX = 300;
// Stacked under the list on narrow screens, the list only needs to show a few rows.
const STACKED_LIST_MAX_HEIGHT_IN_PX = 260;

const jobIdOf = (channel: MessagingChannel) =>
  channel.target && isMessagingChannelTargetReference(channel.target)
    ? channel.target.id
    : null;

type JobConversationsProps = {
  order: Order;
  readOnly?: boolean;
};

/**
 * Labels each row from the order's jobs already loaded for the Jobs tab, falling back to the
 * channel's own name for a job a Dispatch filter hides.
 */
const OrderJobConversations = observer(({ order, readOnly }: JobConversationsProps) => {
  const theme = useTheme();
  const { ordersDispatchStore, userStore } = useStores();
  const viewerUserId = userStore.user?.id;
  const infiniteScroll = useInfiniteScroll({ rootMargin: '200px' });

  const { channels, fetchMore, hasMore, isLoading, isLoadingMore } =
    useMessagingOrderJobChannelsInfiniteQuery({ orderId: order.id });
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);

  // Pinned on arrival, so a live message moving another row to the top leaves the open thread.
  const newestChannelId = channels[0]?.id;
  if (selectedChannelId === null && newestChannelId) {
    setSelectedChannelId(newestChannelId);
  }

  const loadedOrderJobs = ordersDispatchStore.orderJobs.get(order.id) ?? [];
  const loadedJobOf = (channel: MessagingChannel) =>
    loadedOrderJobs.find((job) => job.id === jobIdOf(channel));
  const jobLabelOf = (channel: MessagingChannel) =>
    loadedJobOf(channel)?.jobId ?? channel.name ?? '';
  // Keyed on the driver, so one driver keeps one color across the order's jobs.
  const avatarColorKeyOf = (channel: MessagingChannel) =>
    channel.counterpartMember(viewerUserId)?.principalId ?? channel.id;
  const driverNameOf = (channel: MessagingChannel) =>
    channel.counterpartMember(viewerUserId)?.displayName ??
    t('messaging.order_tab.job_dms.unknown_driver');

  useEffect(() => {
    if (!infiniteScroll.endReached || !hasMore || isLoadingMore) return;

    // Re-arm only once the page lands: the observer fires on a crossing, not a state.
    void fetchMore().finally(infiniteScroll.notify);
  }, [
    hasMore,
    infiniteScroll.endReached,
    infiniteScroll.notify,
    isLoadingMore,
    fetchMore,
  ]);

  const framedBoxSx = {
    border: `1px solid ${theme.semantic.divider}`,
    borderRadius: theme.brandV2.borderRadius,
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          ...framedBoxSx,
          alignItems: 'center',
          display: 'flex',
          // Holds the loaded thread's height, so the order card does not shrink and regrow.
          height: { md: `${TARGET_MESSAGING_PANEL_HEIGHT_IN_PX}px` },
          justifyContent: 'center',
          py: 4,
        }}
        data-test-id="order-job-conversations"
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  // A failed read lands here too, leaving the rest of the order row working.
  if (!channels.length) {
    return (
      <Box sx={{ ...framedBoxSx, p: 3 }} data-test-id="order-job-conversations">
        <EmptyStateMessage message={t('messaging.order_tab.job_dms.no_conversations')} />
      </Box>
    );
  }

  const selectedChannel =
    channels.find((channel) => channel.id === selectedChannelId) ?? channels[0];
  const selectedJobId = jobIdOf(selectedChannel);
  const selectedJobLabel = jobLabelOf(selectedChannel);
  const selectedJobContext = [
    selectedJobLabel,
    order.orderId,
    (loadedJobOf(selectedChannel) ?? order).material?.name,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Box
      data-test-id="order-job-conversations"
      sx={{
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: { md: `${LIST_WIDTH_IN_PX}px minmax(0, 1fr)`, xs: '1fr' },
      }}
    >
      <Box
        sx={{
          ...framedBoxSx,
          display: 'flex',
          flexDirection: 'column',
          height: { md: `${TARGET_MESSAGING_PANEL_HEIGHT_IN_PX}px` },
          maxHeight: { xs: `${STACKED_LIST_MAX_HEIGHT_IN_PX}px`, md: 'none' },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            borderBottom: `1px solid ${theme.semantic.divider}`,
            px: 1.5,
            py: 1.25,
          }}
        >
          <Typography
            sx={{
              color: theme.semantic.textSecondary,
              fontSize: '11px',
              fontWeight: 800,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            {t('messaging.order_tab.sub_tabs.job_dms')}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {channels.map((channel) => (
            <OrderJobConversationRow
              avatarColorKey={avatarColorKeyOf(channel)}
              channel={channel}
              driverName={driverNameOf(channel)}
              isSelected={channel.id === selectedChannel.id}
              jobLabel={jobLabelOf(channel)}
              key={channel.id}
              onSelect={(nextChannel) => setSelectedChannelId(nextChannel.id)}
              viewerUserId={viewerUserId}
            />
          ))}

          <Box ref={infiniteScroll.nodeRef} />

          {isLoadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}
        </Box>
      </Box>

      {selectedJobId && (
        <JobThreadPanel
          canStartConversation={loadedJobOf(selectedChannel)?.messagingEnabled ?? false}
          driverAvatarColorKey={avatarColorKeyOf(selectedChannel)}
          driverName={driverNameOf(selectedChannel)}
          jobContext={selectedJobContext}
          jobId={selectedJobId}
          jobLabel={selectedJobLabel}
          key={selectedChannel.id}
          readOnly={readOnly}
        />
      )}
    </Box>
  );
});

export default OrderJobConversations;
